import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryList } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="text-xl md:text-2xl font-heading font-bold text-foreground-900 tracking-tight">
              Kakobuy<span className="text-primary-500">Finds</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer px-3 py-2 rounded-lg hover:bg-background-100"
              >
                <span>Kategorie</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categoryList.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.namePl}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/artykul-kakobuy-sheet" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
              Przewodnik
            </Link>
            <Link to="/porownanie-kakobuy-cssbuy" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
              Porównanie
            </Link>
            <Link to="/bezpieczenstwo-kakobuy" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
              Bezpieczeństwo
            </Link>
          </div>

          <div className="hidden md:block">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          <button
            onClick={() => { setMobileOpen(!mobileOpen); setMobileCatOpen(false); }}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-3">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-xl bg-accent-100 hover:bg-accent-200 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-accent-800">
                <i className="ri-store-2-line text-lg"></i>
                Kategorie produktów
              </span>
              <i className={`ri-arrow-down-s-line text-accent-600 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="mt-2 grid grid-cols-2 gap-1.5 max-h-[360px] overflow-y-auto pb-1">
                {categoryList.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2.5 py-2.5 text-xs text-foreground-700 bg-background-100 hover:bg-accent-50 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                    {cat.namePl}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="px-4 pb-2">
            <div className="flex flex-col gap-0.5 py-2 border-t border-background-200/60">
              <Link
                to="/artykul-kakobuy-sheet"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-file-text-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                Przewodnik Kakobuy
              </Link>
              <Link
                to="/porownanie-kakobuy-cssbuy"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-scales-3-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                Porównanie platform
              </Link>
              <Link
                to="/bezpieczenstwo-kakobuy"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-shield-check-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                Bezpieczeństwo
              </Link>
            </div>
          </div>

          <div className="px-4 pb-5 pt-2 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}